import { z } from '@/utils/zod'
import { StorageResourceSchema, LanguageSchema, createAddressSchema, type CountryCode } from '@/entities/common'
import { EntitySchema } from '@/entities/Entity'
import { ObjectId } from '@/utils/objectid'
import { advancedReceiptTypesWithoutInvoice, invoiceTypes } from '@/entities/finance/receipt/ReceiptTypes'

import { OrganizationClassificationSchema } from './OrganizationClassification'
import { OrganizationDomainSchema } from './OrganizationDomain'
import { OrganizationZoneSchema } from './OrganizationZone'
import { OrganizationBillingSchema } from './OrganizationBilling'

export const OrganizationShortIdSchema = z.string().min(5).max(5)

// These are the countries that we allow for orgs in general
export const organizationCountries = ['DE', 'LU', 'AT', 'CH', 'US'] as const satisfies readonly CountryCode[]

export type OrganizationCountryCode = (typeof organizationCountries)[number]

export const OrganizationEventimWebhookTypes = [
    'CUSTOMER_COMMUNICATION_UPDATE',
    'CUSTOMER_UPDATE',
    'CUSTOMER_COMMUNICATION_DELETE',
] as const

export const OrganizationEventimWebhookTypeSchema = z.enum(OrganizationEventimWebhookTypes)

export type OrganizationEventimWebhookType = z.infer<typeof OrganizationEventimWebhookTypeSchema>

export const ReplyToReceiptTypeSchema = z.enum([...invoiceTypes, ...advancedReceiptTypesWithoutInvoice])

export type ReplyToReceiptType = z.infer<typeof ReplyToReceiptTypeSchema>

export const OrganizationEventimSchema = z.object({
    key: z.string().min(1),
    secret: z.string().min(1),
    webhooks: z.record(OrganizationEventimWebhookTypeSchema, z.string().min(1)),
})

export type OrganizationEventim = z.infer<typeof OrganizationEventimSchema>

export const ReplyToSettingsSchema = z.object({
    defaultReplyToAddress: z.email().nullable(),
    specificReplyToAddresses: z
        .object({
            receipts: z.partialRecord(ReplyToReceiptTypeSchema, z.email()),
        })
        .nullable(),
    allowedAddresses: z.array(z.email()).max(50),
})

export type ReplyToSettings = z.infer<typeof ReplyToSettingsSchema>

export const OrganizationSchema = EntitySchema.extend({
    shortId: OrganizationShortIdSchema,

    //
    // Name, info and branding
    //

    name: z.string().min(1).max(120),
    website: z.union([z.literal(''), z.url()]),
    foundingYear: z.number().int().nullable(),
    // This is the member count of the current organization
    memberCount: z.number().int(),
    // This is the total member count of all members from child organizations
    childrenMemberCount: z.number().int(),
    userCount: z.number().int(),
    avatar: StorageResourceSchema.nullable(),
    hasPublicConnectProfile: z.boolean(),
    address: createAddressSchema({ allowEmpty: false, countries: organizationCountries }),
    brand: z.object({
        // for now we keep as is it, but once we get rid of the old campai, we might remove the "textColor"
        // and rename the "backgroundColor" to "color"
        backgroundColor: z.hexColor().nullable(),
        textColor: z.hexColor().nullable(),
    }),
    classification: OrganizationClassificationSchema.nullish(),
    language: LanguageSchema,

    //
    // Hierarchy settings
    //
    zone: OrganizationZoneSchema,
    parentOrganizations: z.array(z.objectId()),
    childrenCount: z.record(z.string(), z.number()),

    //
    // Customization
    //
    domains: z.array(OrganizationDomainSchema),
    replyToSettings: ReplyToSettingsSchema,
    slug: z.string().min(1),
    checks: z.record(z.string(), z.boolean()),
    useSingleCommunicationTemplate: z.boolean(),

    //
    // Billing & Services
    //
    billing: OrganizationBillingSchema,
    credit: z.number(),
    eventim: OrganizationEventimSchema.nullable(),
    verified: z.boolean().nullish(),
    onlineLetterSending: z.boolean(),
    // notAvailable = not available yet, available = used but also show old admin, replace = only new crm available
    newCRMMode: z.enum(['notAvailable', 'available', 'replace']),
    migrationStatus: z.enum(['available', 'inProgress', 'finished']).nullish(),
    // The date when the organization was migrated to the new CRM
    migratedAt: z.date().nullish(),

    //
    // External accounts
    //
    expressaTenant: z.objectId(),
    stripeAccount: z.string(),
    paypalAccount: z.string(),

    //
    // Internal stuff
    //
    encryptionKey: z.string(),
})

export type Organization = z.infer<typeof OrganizationSchema>

export const DEMO_ORGANIZATION_ID = new ObjectId('c0ffeec0ffeec0ffeec0ffee')
